import type { ICommand } from '../../commands/ICommand';
import type {
  ScenarioDelta,
  NodeAddition,
  NodeRemoval,
  NodeMove,
  NodeUpdate,
  EdgeAddition,
  EdgeRemoval,
} from './ScenarioDelta';

export interface CommandDeltaContribution<TProps = Record<string, unknown>, TChange = unknown> {
  additions?: Array<NodeAddition<TProps>>;
  removals?: Array<NodeRemoval<TProps>>;
  moves?: Array<NodeMove>;
  updates?: Array<NodeUpdate<TChange>>;
  edgeAdditions?: Array<EdgeAddition>;
  edgeRemovals?: Array<EdgeRemoval>;
}

export interface IScenarioRecordable<TProps = Record<string, unknown>, TChange = unknown> extends ICommand {
  readonly isScenarioRecordable: true;
  toScenarioDelta: () => CommandDeltaContribution<TProps, TChange>;
  getUndoDelta: () => CommandDeltaContribution<TProps, TChange>;
}

export function isScenarioRecordable(
  command: ICommand
): command is IScenarioRecordable {
  return (
    (command as IScenarioRecordable).isScenarioRecordable === true &&
    typeof (command as IScenarioRecordable).toScenarioDelta === 'function'
  );
}

/** Field name of a change, when it follows the {field, oldValue, newValue} shape. */
function changeField(change: unknown): string | null {
  if (!change || typeof change !== 'object') return null;
  const field = (change as { field?: unknown }).field;
  return typeof field === 'string' ? field : null;
}

function sameValue(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

/**
 * Collapse repeated edits of the same node+field into a single update: keep the
 * original `oldValue`, take the latest `newValue`, and drop the entry entirely
 * when the value has been set back to what it started as.
 *
 * Without this, one property-panel edit counts twice — the field renderer
 * commits on both change and blur — inflating the change count and producing a
 * duplicate no-op intent when the scenario is applied.
 */
function mergeUpdates<TChange>(
  existing: ReadonlyArray<NodeUpdate<TChange>>,
  incoming: ReadonlyArray<NodeUpdate<TChange>>,
): Array<NodeUpdate<TChange>> {
  const merged = [...existing];
  for (const update of incoming) {
    const field = changeField(update.change);
    const index = field === null
      ? -1
      : merged.findIndex(
          (candidate) => candidate.nodeId === update.nodeId && changeField(candidate.change) === field,
        );
    if (index === -1) {
      merged.push(update);
      continue;
    }
    const priorOldValue = (merged[index].change as { oldValue?: unknown }).oldValue;
    const nextNewValue = (update.change as { newValue?: unknown }).newValue;
    if (sameValue(priorOldValue, nextNewValue)) {
      merged.splice(index, 1);
      continue;
    }
    merged[index] = {
      ...merged[index],
      ...update,
      change: { ...(update.change as object), oldValue: priorOldValue } as TChange,
    };
  }
  return merged;
}

export function mergeDeltas<TProps = Record<string, unknown>, TChange = unknown>(
  existing: ScenarioDelta<TProps, TChange>,
  contribution: CommandDeltaContribution<TProps, TChange>
): ScenarioDelta<TProps, TChange> {
  return {
    additions: [...existing.additions, ...(contribution.additions ?? [])],
    removals: [...existing.removals, ...(contribution.removals ?? [])],
    moves: [...existing.moves, ...(contribution.moves ?? [])],
    updates: mergeUpdates(existing.updates, contribution.updates ?? []),
    edgeAdditions: [...existing.edgeAdditions, ...(contribution.edgeAdditions ?? [])],
    edgeRemovals: [...existing.edgeRemovals, ...(contribution.edgeRemovals ?? [])],
  };
}

export function unmergeDeltas<TProps = Record<string, unknown>, TChange = unknown>(
  existing: ScenarioDelta<TProps, TChange>,
  contribution: CommandDeltaContribution<TProps, TChange>
): ScenarioDelta<TProps, TChange> {
  const additionIds = new Set(contribution.additions?.map((a) => a.id) ?? []);
  const removalIds = new Set(contribution.removals?.map((r) => r.nodeId) ?? []);
  const moveIds = new Set(contribution.moves?.map((m) => m.nodeId) ?? []);
  const updateIds = new Set(contribution.updates?.map((u) => u.nodeId) ?? []);
  const edgeAdditionIds = new Set(contribution.edgeAdditions?.map((e) => e.id) ?? []);
  const edgeRemovalIds = new Set(contribution.edgeRemovals?.map((e) => e.edgeId) ?? []);

  return {
    additions: existing.additions.filter((a) => !additionIds.has(a.id)),
    removals: existing.removals.filter((r) => !removalIds.has(r.nodeId)),
    moves: existing.moves.filter((m) => !moveIds.has(m.nodeId)),
    updates: existing.updates.filter((u) => !updateIds.has(u.nodeId)),
    edgeAdditions: existing.edgeAdditions.filter((e) => !edgeAdditionIds.has(e.id)),
    edgeRemovals: existing.edgeRemovals.filter((e) => !edgeRemovalIds.has(e.edgeId)),
  };
}
